/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Button } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY   = '#1a3a5c';
const COLORS = ['#1a3a5c','#2e86c1','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2','#fa8c16','#389e0d','#cf1322'];
const toN    = v => Number(v || 0);
const fmtE   = v => `E ${toN(v).toLocaleString()}`;

function KPI({ label, value, color=NAVY, sub }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height=280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

export default function FleetAnalyticsProgrammeCost() {
    const [tco,     setTco]     = useState([]);
    const [status,  setStatus]  = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                FleetService.reportTCO(),
                FleetService.reportStatus(),
            ]);
            if (Number(r1?.data?.code) === 200) setTco(r1.data.result    || []);
            if (Number(r2?.data?.code) === 200) setStatus(r2.data.result || []);
        } catch (e) { message.error('Failed to load data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    // Group by programme
    const byProgramme = useMemo(() => {
        const m = {};
        tco.forEach(r => {
            const k = r.assigned_programme || 'Unassigned';
            if (!m[k]) m[k] = { programme:k, vehicles:0, procurement:0, fuel:0, repair:0, totalCost:0, totalKm:0 };
            m[k].vehicles++;
            m[k].procurement += toN(r.procurement_cost);
            m[k].fuel        += toN(r.fuel_cost);
            m[k].repair      += toN(r.repair_cost);
            m[k].totalCost   += toN(r.total_cost);
            m[k].totalKm     += toN(r.total_km);
        });
        return Object.values(m).map(p => ({
            ...p,
            cpk:      p.totalKm > 0 ? +(p.totalCost / p.totalKm).toFixed(2) : 0,
            cpvehicle: p.vehicles > 0 ? Math.round(p.totalCost / p.vehicles) : 0,
        })).sort((a,b) => b.totalCost - a.totalCost);
    }, [tco]);

    // Fleet status by programme
    const statusByProg = useMemo(() => {
        const m = {};
        status.forEach(v => {
            const k = v.assigned_programme || 'Unassigned';
            if (!m[k]) m[k] = { programme:k, good:0, fair:0, repairs:0, disposal:0, total:0 };
            m[k].total++;
            const s = (v.functional_status||'').toLowerCase();
            if (/good/i.test(s)) m[k].good++;
            else if (/fair/i.test(s)) m[k].fair++;
            else if (/repair/i.test(s)) m[k].repairs++;
            else if (/dispos/i.test(s)) m[k].disposal++;
        });
        return Object.values(m).sort((a,b) => b.total - a.total);
    }, [status]);

    const programmes    = byProgramme.length;
    const totalCost     = byProgramme.reduce((s,p) => s+p.totalCost, 0);
    const totalVehicles = byProgramme.reduce((s,p) => s+p.vehicles, 0);
    const unassigned    = (byProgramme.find(p=>p.programme==='Unassigned')?.vehicles) || 0;

    // Cost breakdown pie
    const costPie = byProgramme.map((p,i) => ({
        name: p.programme, value: p.totalCost, fill: COLORS[i % COLORS.length]
    }));

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(byProgramme.map(p => ({
            'Programme':      p.programme,
            'Vehicles':       p.vehicles,
            'Procurement (E)':p.procurement,
            'Fuel Cost (E)':  p.fuel,
            'Repair Cost (E)':p.repair,
            'Total Cost (E)': p.totalCost,
            'Total KM':       p.totalKm,
            'Cost/KM (E)':    p.cpk,
            'Cost/Vehicle (E)':p.cpvehicle,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Programme Costs');
        saveAs(new Blob([XLSX.write(wb, { bookType:'xlsx', type:'array' })]),
            `Fleet_ProgrammeCosts_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Programme Cost Analysis</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Fleet costs broken down by MoH programme · cost-per-vehicle · operational efficiency
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<ReloadOutlined />} onClick={load}/>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!byProgramme.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* KPIs */}
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                    <KPI label="Programmes"        value={programmes}           color={NAVY}/>
                    <KPI label="Total Vehicles"    value={totalVehicles}        color="#2e86c1"/>
                    <KPI label="Total Fleet Cost"  value={fmtE(totalCost)}      color="#722ed1"/>
                    <KPI label="Unassigned Vehicles" value={unassigned}         color={unassigned>0?'#fa8c16':'#52c41a'} sub="no programme tagged"/>
                </div>

                {/* Row 1: Stacked cost + pie */}
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
                    <ChartCard title="Total Cost by Programme — Fuel + Repair + Procurement (E)" height={300}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byProgramme.map(p=>({ name:p.programme, Procurement:p.procurement, Fuel:p.fuel, Repair:p.repair }))}
                                margin={{ top:5, right:10, left:10, bottom:40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={55}/>
                                <YAxis tick={{ fontSize:10 }} tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`, n]}/>
                                <Legend wrapperStyle={{ fontSize:10 }}/>
                                <Bar dataKey="Procurement" stackId="a" fill={COLORS[0]}/>
                                <Bar dataKey="Fuel"        stackId="a" fill={COLORS[2]}/>
                                <Bar dataKey="Repair"      stackId="a" fill={COLORS[4]} radius={[4,4,0,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Cost Share by Programme" height={300}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={costPie} dataKey="value" nameKey="name"
                                    cx="50%" cy="45%" outerRadius={100} labelLine={false}
                                    label={({ percent }) => percent>0.05?`${(percent*100).toFixed(0)}%`:''}>
                                    {costPie.map((d,i) => <Cell key={i} fill={d.fill}/>)}
                                </Pie>
                                <Tooltip formatter={v=>[`E ${toN(v).toLocaleString()}`, 'Total Cost']}/>
                                <Legend iconSize={9} wrapperStyle={{ fontSize:10 }}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Row 2: cost per vehicle + fleet condition */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <ChartCard title="Cost per Vehicle by Programme (E)" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byProgramme.map(p=>({ name:p.programme, cpv:p.cpvehicle, vehicles:p.vehicles }))}
                                margin={{ top:5, right:50, left:10, bottom:40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={55}/>
                                <YAxis tick={{ fontSize:10 }} tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`, 'Cost/Vehicle']}/>
                                <Bar dataKey="cpv" name="Cost/Vehicle" fill={NAVY} radius={[4,4,0,0]}>
                                    <LabelList dataKey="cpv" position="top" style={{ fontSize:9 }} formatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Fleet Condition by Programme" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={statusByProg}
                                margin={{ top:5, right:10, left:10, bottom:40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="programme" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={55}/>
                                <YAxis tick={{ fontSize:10 }} allowDecimals={false}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{ fontSize:10 }}/>
                                <Bar dataKey="good"     name="Good"     stackId="a" fill="#52c41a"/>
                                <Bar dataKey="fair"     name="Fair"     stackId="a" fill="#faad14"/>
                                <Bar dataKey="repairs"  name="Repairs"  stackId="a" fill="#f5222d"/>
                                <Bar dataKey="disposal" name="Disposal" stackId="a" fill="#722ed1" radius={[4,4,0,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Programme detail table */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                        <thead>
                            <tr style={{ background:NAVY, color:'#fff' }}>
                                {['Programme','Vehicles','Procurement','Fuel','Repair','Total Cost','Cost/Vehicle','Cost/KM'].map(h =>
                                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:600 }}>{h}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {byProgramme.map((p,i) => (
                                <tr key={i} style={{ background:i%2===0?'#fff':'#f9f9f9', borderBottom:'1px solid #f0f0f0' }}>
                                    <td style={{ padding:'9px 12px', fontWeight:600 }}>{p.programme}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right' }}>{p.vehicles}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right' }}>{fmtE(p.procurement)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right' }}>{fmtE(p.fuel)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right', color:'#cf1322' }}>{fmtE(p.repair)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:700 }}>{fmtE(p.totalCost)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right' }}>{fmtE(p.cpvehicle)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right' }}>{p.cpk > 0 ? `E ${p.cpk}` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && byProgramme.length === 0 && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8, marginTop:14 }}>
                        No programme data. Tag vehicles to programmes in the asset form.
                    </div>
                )}
            </Spin>
        </div>
    );
}